import React, { useState } from 'react';
import { Clock, Lock, Mail, MessageCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

// O ACESSO DO CLIENTE (PROMPT 201): o aviso perto de vencer e na carência, e a tela de quem não entra pela Arkipel
// (vencida, sem pagamento ou suspensa). O cliente de transferência ou de cortesia nunca vê o Stripe. Nada do que ele fez
// se perde: a tela só barra a entrada, e tudo volta quando o acesso volta.

/** O contato de antes do Backoffice (o dos Termos); o servidor manda o do Backoffice (LOTE 204, A4). */
export const CONTATO_PADRAO = 'contato@arkipel.io';

/** "AAAA-MM-DD" em "DD/MM/AAAA", sem passar por fuso (a data é a do calendário). */
export const dataCurta = (iso) => {
  const [a, m, d] = String(iso || '').slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : '';
};

export const temAcesso = (account) => {
  if (account?.acesso) return Boolean(account.acesso.entra);
  return account?.subscription_status === 'active';      // conta guardada antes do PROMPT 201
};

const textoDoAviso = (acesso) => {
  if (acesso?.situacao === 'em_carencia') {
    return `Seu acesso venceu em ${dataCurta(acesso.vence_em)}. Renove até ${dataCurta(acesso.carencia_ate)} para continuar usando a plataforma.`;
  }
  if (acesso?.dias_para_vencer === 0) return 'Seu acesso vence hoje.';
  return `Seu acesso vence em ${dataCurta(acesso?.vence_em)}.`;
};

/** O aviso no canto, perto de vencer e na carência. Some até a próxima sessão quando a pessoa fecha. */
export function AvisoDeAcesso({ acesso }) {
  const chave = `aviso-de-acesso:${acesso?.situacao}:${acesso?.vence_em}`;
  const [fechado, setFechado] = useState(() => {
    try { return sessionStorage.getItem(chave) === '1'; } catch { return false; }
  });
  if (!acesso?.aviso || fechado) return null;
  const fechar = () => {
    try { sessionStorage.setItem(chave, '1'); } catch { /* sem armazenamento: só some agora */ }
    setFechado(true);
  };
  return (
    <div role="status" data-testid="aviso-de-acesso"
      className="fixed inset-x-4 bottom-4 z-50 rounded-lg border border-warning/40 bg-card p-4 shadow-lg sm:left-auto sm:right-4 sm:max-w-sm">
      <div className="flex items-start gap-3">
        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-1 text-sm">
          <p className="font-medium text-foreground">{textoDoAviso(acesso)}</p>
          <p className="text-muted-foreground">
            Fale com a Arkipel para renovar: <a className="font-medium text-foreground underline underline-offset-2" href={`mailto:${acesso.contato}`}>{acesso.contato}</a>
            {acesso.whatsapp_link && (
              <> ou <a className="font-medium text-foreground underline underline-offset-2" href={acesso.whatsapp_link} target="_blank" rel="noreferrer">WhatsApp</a></>
            )}
          </p>
        </div>
        <button type="button" onClick={fechar} aria-label="Fechar o aviso" className="rounded p-1 text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** A tela de quem não entra pela Arkipel: vencida (passada a carência), sem pagamento ou suspensa. */
export function TelaDaArkipel({ acesso, onSair }) {
  const suspensa = acesso?.situacao === 'suspensa';
  const titulo = suspensa ? 'Seu acesso está suspenso.' : 'Seu acesso venceu.';
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-16" data-testid="tela-fale-com-a-arkipel">
      <div className="w-full max-w-md space-y-6 rounded-2xl border border-border bg-card p-8 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Lock className="h-6 w-6 text-foreground" aria-hidden="true" />
        </span>
        <div className="space-y-2">
          <h1 className="font-heading text-2xl font-semibold text-foreground">{titulo}</h1>
          <p className="text-base text-muted-foreground">{suspensa ? 'Fale com a Arkipel para reativar.' : 'Fale com a Arkipel para renovar.'}</p>
          <p className="text-sm text-muted-foreground">Tudo o que você criou continua guardado e volta assim que o acesso for renovado.</p>
        </div>
        <Button asChild className="w-full">
          <a href={`mailto:${acesso?.contato || CONTATO_PADRAO}`} data-testid="contato-da-arkipel">
            <Mail className="h-4 w-4" aria-hidden="true" />{acesso?.contato || CONTATO_PADRAO}
          </a>
        </Button>
        {acesso?.whatsapp_link && (
          <Button asChild variant="outline" className="w-full">
            <a href={acesso.whatsapp_link} target="_blank" rel="noreferrer" data-testid="whatsapp-da-arkipel">
              <MessageCircle className="h-4 w-4" aria-hidden="true" />WhatsApp da Arkipel
            </a>
          </Button>
        )}
        {onSair && (
          <button type="button" onClick={onSair} className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground">
            Sair
          </button>
        )}
      </div>
    </div>
  );
}
